import { useState, useEffect, useRef } from "react";
import { RefreshCw, Filter, Target, Upload, Trash2, CloudDownload, Clock, CheckCircle } from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl() + "/api";

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const safeJson = async (res) => {
  try { return await res.json(); } catch (e) {
    if (e.message?.includes("body stream already read") || e.message?.includes("locked")) {
      try { return await res.clone().json(); } catch { return { error: "json_parse_error" }; }
    }
    return { error: "json_parse_error", detail: e.message };
  }
};


export default function GoogleAdsROI() {
  const [data, setData] = useState(null);
  const [sources, setSources] = useState(["INTERNE GADS"]);
  const [uploading, setUploading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
    loadSyncStatus();
  }, [sources]);

  const loadData = () => {
    const params = new URLSearchParams();
    if (sources.length) params.set("sources", sources.join(","));
    authFetch(API + "/google-ads/roi?" + params).then(r => safeJson(r)).then(setData).catch(() => {});
  };

  const loadSyncStatus = () => {
    authFetch(API + "/google-ads/sync-status").then(r => safeJson(r)).then(setSyncStatus).catch(() => {});
  };

  const handleSyncApi = async () => {
    setSyncing(true);
    try {
      const res = await authFetch(API + "/google-ads/sync-spend", { method: "POST" });
      const result = await safeJson(res);
      
      if (result.success) {
        alert(`Synchronisation terminee !\n\n${result.new_months} nouveaux mois\n${result.updated_months} mois mis a jour\n\nTotal: ${result.total_amount.toFixed(2)} EUR`);
        loadData();
        loadSyncStatus();
      } else {
        alert("Erreur: " + (result.detail || "Sync echouee"));
      }
    } catch (err) {
      console.error("handleSyncApi error:", err);
    }
    setSyncing(false);
  };

  const handlePurge = async () => {
    if (!window.confirm("Supprimer TOUTES les factures Google Ads ?\n\nCette action est irreversible.")) {
      return;
    }
    
    setPurging(true);
    try {
      const res = await authFetch(API + "/google-ads/invoices/purge", {
        method: "DELETE"
      });
      const result = await safeJson(res);
      
      if (result.success) {
        alert(`${result.deleted} factures supprimees.\n\nVous pouvez re-synchroniser via l'API.`);
        loadData();
      } else {
        alert("Erreur: " + (result.detail || "Purge echouee"));
      }
    } catch (err) {
      console.error("handlePurge error:", err);
    }
    setPurging(false);
  };

  const handleZipUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await authFetch(API + "/google-ads/invoices/import-zip", {
        method: "POST",
        body: formData
      });
      const result = await safeJson(res);
      
      if (result.success) {
        alert(`Import termine !\n\n${result.imported} factures importees\n${result.skipped} deja existantes\n\nTotal: ${result.total_amount.toFixed(2)} EUR`);
        loadData();
      } else {
        alert("Erreur: " + (result.detail || "Import echoue"));
      }
    } catch (err) {
      alert("Erreur lors de l'import: " + err.message);
    }
    setUploading(false);
    e.target.value = "";
  };

  const fmt = v => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const fmtMonth = m => m ? ["Jan","Fev","Mar","Avr","Mai","Juin","Juil","Aout","Sep","Oct","Nov","Dec"][parseInt(m.split("-")[1])-1] + " " + m.split("-")[0] : "";

  const formatSyncDate = (isoStr) => {
    if (!isoStr) return null;
    const d = new Date(isoStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (!data) return <div className="py-8 text-center animate-pulse">Chargement...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="text-xl font-bold" data-testid="roi-title">ROI Google Ads</h2>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handleSyncApi}
            disabled={syncing}
            className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg text-sm font-medium transition-colors"
            data-testid="sync-api-btn"
            title="Synchroniser les depenses depuis l'API Google Ads"
          >
            <CloudDownload size={16} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Sync..." : "Sync API"}
          </button>
          <button
            onClick={handlePurge}
            disabled={purging}
            className="flex items-center gap-2 px-3 py-2 bg-red-600/80 hover:bg-red-700 disabled:opacity-50 rounded-lg text-sm font-medium transition-colors"
            data-testid="purge-btn"
            title="Supprimer toutes les factures"
          >
            <Trash2 size={16} />
            {purging ? "..." : "Purger"}
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-3 py-2 bg-slate-600/80 hover:bg-slate-700 disabled:opacity-50 rounded-lg text-sm font-medium transition-colors"
            data-testid="import-zip-btn"
          >
            <Upload size={16} />
            {uploading ? "Import..." : "Import ZIP"}
          </button>
        </div>
      </div>

      {/* Sync status indicator */}
      {syncStatus && (
        <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="sync-status">
          {syncStatus.synced ? (
            <>
              <CheckCircle size={12} className="text-emerald-400" />
              <span>Derniere sync API : {formatSyncDate(syncStatus.last_synced_at)}</span>
            </>
          ) : (
            <>
              <Clock size={12} className="text-amber-400" />
              <span>Aucune synchronisation API effectuee — cliquez "Sync API" pour demarrer</span>
            </>
          )}
        </div>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept=".zip"
        onChange={handleZipUpload}
        className="hidden"
      />
      
      <div className="bg-slate-800/50 p-3 rounded-lg">
        <div>
          <p className="text-xs text-slate-400 mb-1">Sources</p>
          <div className="flex flex-wrap gap-1">
            {data.filters?.sources?.map(s => (
              <button key={s} onClick={() => setSources(sources.includes(s) ? sources.filter(x=>x!==s) : [...sources,s])} 
                className={"px-2 py-0.5 rounded text-xs " + (sources.includes(s) ? "bg-blue-600" : "bg-slate-700")}>{s}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 text-center">
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
          <p className="text-xs text-slate-400">Depenses</p>
          <p className="text-lg font-bold text-amber-400" data-testid="total-spend">{fmt(data.total_spend)}</p>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
          <p className="text-xs text-slate-400">R1 Confirmes</p>
          <p className="text-lg font-bold text-green-400" data-testid="r1-count">{data.r1_filtered}</p>
        </div>
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 ring-1 ring-purple-500">
          <p className="text-xs text-slate-400">Cout/R1</p>
          <p className="text-lg font-bold text-purple-400" data-testid="cost-per-r1">{fmt(data.cost_per_r1)}</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
          <p className="text-xs text-slate-400">Taux conversion</p>
          <p className="text-lg font-bold text-blue-400" data-testid="conversion-rate">{data.conversion_rate}%</p>
        </div>
      </div>

      {data.monthly_data?.length > 0 && (
        <div className="bg-slate-800/50 rounded-lg p-3">
          <h3 className="font-semibold mb-2">Evolution mensuelle</h3>
          <table className="w-full text-sm">
            <thead><tr className="text-slate-400 border-b border-slate-700">
              <th className="text-left py-1 w-20">Mois</th>
              <th className="text-right py-1 w-24">Depenses</th>
              <th className="text-right py-1 w-12">R1</th>
              <th className="text-right py-1 w-24">Cout/R1</th>
              <th className="text-left py-1 pl-3">Telepros</th>
              <th className="text-left py-1 pl-3">Techniciens</th>
            </tr></thead>
            <tbody>
              {data.monthly_data.slice().reverse().slice(0,24).map(m => (
                <tr key={m.month} className="border-b border-slate-700/30">
                  <td className="py-2 w-20">{fmtMonth(m.month)}</td>
                  <td className="py-2 text-right w-24 text-amber-400">{fmt(m.spend)}</td>
                  <td className="py-2 text-right w-12 text-green-400">{m.r1}</td>
                  <td className="py-2 text-right w-24 text-purple-400">{m.cost_per_r1 > 0 ? fmt(m.cost_per_r1) : "-"}</td>
                  <td className="py-2 pl-3"><StackedBar data={m.telepros} /></td>
                  <td className="py-2 pl-3"><StackedBar data={m.techniciens} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StackedBar({ data }) {
  if (!data || data.length === 0) return <span className="text-slate-500">-</span>;
  
  const getColor = (name) => {
    const palette = [
      "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#f97316", 
      "#eab308", "#22c55e", "#06b6d4", "#3b82f6", "#6366f1",
      "#14b8a6", "#a855f7", "#d946ef", "#0ea5e9", "#84cc16",
      "#f43f5e", "#10b981", "#fbbf24", "#fb923c", "#7c3aed",
      "#2dd4bf", "#f472b6", "#facc15", "#4ade80", "#38bdf8",
      "#c084fc", "#fb7185", "#a3e635", "#34d399", "#60a5fa"
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = ((hash << 7) - hash + name.charCodeAt(i) * (i + 1)) | 0;
    }
    return palette[Math.abs(hash) % palette.length];
  };
  
  return (
    <div className="w-full bg-slate-700/30 rounded-full h-8 overflow-hidden flex">
      {data.map((item, i) => (
        <div 
          key={i}
          className="h-full flex items-center justify-center overflow-hidden border-r border-slate-900/30 last:border-0"
          style={{ width: `${item.pct}%`, backgroundColor: getColor(item.name), minWidth: item.pct > 0 ? '24px' : '0' }}
          title={`${item.name}: ${item.pct}%`}
        >
          <span className="text-xs text-white font-semibold truncate px-1 drop-shadow-md">
            {item.pct >= 12 ? item.name : ''} {item.pct >= 8 ? `${item.pct}%` : ''}
          </span>
        </div>
      ))}
    </div>
  );
}
